import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { apiCall } from '../../utils/fetchAPI';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { POST_TYPES } from '../../constants/community';
import { translateApiMessage } from '../../utils/apiMessages';
import { usePhotoUploads } from '../../hooks/community/usePhotoUploads';
import PhotoPicker from './PhotoPicker';

const EMPTY = { type: '', title: '', body: '', neighborhoodUuid: '' };

/**
 * Writing to the feed.
 *
 * The type is asked for first and is required: it is what keeps the feed
 * readable, so it cannot be an afterthought at the bottom of the form.
 */
function PostComposer({ neighborhoods = [], onPublished }) {
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const photos = usePhotoUploads();

  if (!isAuthenticated) {
    return null;
  }

  const update = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const close = () => {
    setOpen(false);
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    setError('');

    try {
      const response = await apiCall('/posts', {
        method: 'POST',
        body: JSON.stringify({
          type: form.type,
          title: form.title.trim(),
          body: form.body.trim(),
          neighborhoodUuid: form.neighborhoodUuid || null,
          mediaUuids: photos.uuids,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(translateApiMessage(data.error) || 'Votre publication n’a pas pu être envoyée.');
        return;
      }

      toast.success('Votre publication est en ligne.');
      setForm(EMPTY);
      photos.reset();
      close();
      onPublished?.(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Named for what it starts, not for what it ends in: "Publier" on a
          button that opens an empty form promises the wrong thing. */}
      <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
        Écrire un message
      </Button>

      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <form onSubmit={submit}>
          <DialogTitle>Écrire au quartier</DialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <TextField
                select
                label="De quoi s’agit-il ?"
                value={form.type}
                onChange={update('type')}
                required
                disabled={sending}
                helperText="C’est ce qui permet à vos voisins de s’y retrouver dans le fil."
              >
                {POST_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Titre"
                value={form.title}
                onChange={update('title')}
                required
                disabled={sending}
                inputProps={{ maxLength: 180 }}
              />

              <TextField
                label="Message"
                value={form.body}
                onChange={update('body')}
                required
                disabled={sending}
                multiline
                minRows={5}
                inputProps={{ maxLength: 5000 }}
              />

              {neighborhoods.length > 0 && (
                <TextField
                  select
                  label="Quartier (facultatif)"
                  value={form.neighborhoodUuid}
                  onChange={update('neighborhoodUuid')}
                  disabled={sending}
                  helperText="Laissez vide si cela concerne toute la ville."
                >
                  <MenuItem value="">Toute la ville</MenuItem>
                  {neighborhoods.map((neighborhood) => (
                    <MenuItem key={neighborhood.uuid} value={neighborhood.uuid}>
                      {neighborhood.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <PhotoPicker uploads={photos} disabled={sending} />

              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={close} disabled={sending} variant="outlined">
              Annuler
            </Button>
            {/* Waits for the photos still on their way: sent now, the post
                would go out without them. */}
            <Button type="submit" variant="contained" disabled={sending || photos.uploading}>
              {photos.uploading ? 'Envoi des photos…' : 'Envoyer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

export default PostComposer;
